import { useCounter } from '../hooks/useCounter'

// Animated Stat Component
const AnimatedStat = ({ value, label, description, color }: { 
  value: string; 
  label: string; 
  description: string; 
  color: string 
}) => {
  // Handle different formats: "24/7", "90%", "10x"
  let numericValue = 0
  let displayValue = value

  if (value.includes('/')) {
    // Handle "24/7" format
    const nums = value.split('/')
    numericValue = parseInt(nums[0])
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}/${nums[1]}`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  } else if (value.includes('%')) {
    // Handle "90%" format
    numericValue = parseInt(value.replace('%', ''))
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}%`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  } else if (value.includes('x')) {
    // Handle "10x" format
    numericValue = parseInt(value.replace('x', ''))
    const { count, ref } = useCounter({ end: numericValue, duration: 2000, startOnView: true })
    displayValue = `${count}x`
    
    return (
      <div ref={ref} className="stat-item">
        <div className={`text-4xl font-bold ${color} mb-2`}>{displayValue}</div>
        <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
        <p className="text-gray-600">{description}</p>
      </div>
    )
  }

  // Fallback for other formats
  return (
    <div className="stat-item">
      <div className={`text-4xl font-bold ${color} mb-2`}>{value}</div>
      <div className="text-lg text-gray-700 font-semibold mb-2">{label}</div>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

const Introduction = () => {
  return (
    <section id="Introduction" className="section introduction py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Why AI Agents Are{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                The Future
              </span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              Traditional SaaS solutions require human intervention at every step. AI agents work autonomously, 
              learning from data, making decisions, and executing tasks without constant oversight.
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <AnimatedStat
              value="24/7"
              label="Uptime"
              description="AI agents never sleep, ensuring continuous productivity and monitoring."
              color="text-blue-600"
            />
            <AnimatedStat
              value="90%"
              label="Cost Reduction"
              description="Reduce operational costs by automating repetitive tasks and processes."
              color="text-purple-600"
            />
            <AnimatedStat
              value="10x"
              label="Production"
              description="AI agents process information and execute tasks at superhuman speed."
              color="text-indigo-600"
            />
          </div>

          {/* Key Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Autonomous Decision Making */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/autonomous-decision.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Autonomous Decision Making</h3>
                <p className="text-white">
                  AI agents analyze data, identify patterns, and make intelligent decisions without human intervention.
                </p>
              </div>
            </div>

            {/* Continuous Learning */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/continuous-learning.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Continuous Learning</h3>
                <p className="text-white">
                  Every interaction makes them smarter, improving performance and accuracy over time.
                </p>
              </div>
            </div>

            {/* Error-Free Execution */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/error-free-execution.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Error-Free Execution</h3>
                <p className="text-white">
                  Eliminate human errors with precise, consistent execution of complex workflows.
                </p>
              </div>
            </div>

            {/* Infinite Scalability */}
            <div className="benefit-card relative overflow-hidden p-8 rounded-2xl text-center group cursor-pointer">
              <div 
                className="absolute inset-0 bg-cover bg-center transition-transform duration-500 ease-out group-hover:scale-110"
                style={{
                  backgroundImage: 'url(/images/infinite-scalability.jpg)'
                }}
              ></div>
              <div className="absolute inset-0 bg-blue-900 opacity-50"></div>
              <div className="absolute inset-0 bg-black opacity-20"></div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-white mb-3">Infinite Scalability</h3>
                <p className="text-white">
                  Scale your operations without hiring more staff or increasing infrastructure costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Introduction
